/**
 * Canonical v1 Events & Alerts payload mappers.
 *
 * Source of truth: FLYsafe MQTT Topic doc (docs.airmarket.dev/FLYsafe_MQTT_Topic).
 *   Events  → v1/{tenant}/aircraft/{serial}/drones/events
 *   Alerts  → v1/{tenant}/aircraft/{serial}/alerts[/{severity}]
 *   Broadcast → v1/{tenant}/shared/alerts/broadcast
 *
 * Both map into the internal `EventEnvelope` so the existing Events tab
 * (drone detail) + Live Alerts pane + DaaConflictModal render them with no
 * UI changes — they all read `useAlertsStore`, keyed by `drone_id`.
 */
import { AlertEnvelope, type AlertSeverity } from '@/lib/mqtt/schemas/alerts';
import { EventEnvelope } from '@/lib/mqtt/schemas/events';
import { z } from 'zod';

/* ── Doc payloads (lenient — validate shape, tolerate extra fields) ── */

const DocEventPayload = z.object({
  ts: z.number(), // Unix ms
  droneId: z.string(),
  tenantId: z.string().optional(),
  category: z.string(),
  severity: z.string(),
  eventType: z.string().optional().default('event'),
  message: z.string(),
  data: z.record(z.unknown()).optional(),
});

const DocAlertPayload = z.object({
  ts: z.number(),
  alertId: z.string(),
  droneId: z.string().nullable().optional(),
  tenantId: z.string().optional(),
  severity: z.string(),
  category: z.string(),
  title: z.string().optional(),
  message: z.string(),
  sourceEvent: z.string().optional(),
  requiresAck: z.boolean().optional(),
  ackedAt: z.number().nullable().optional(),
  ackedBy: z.string().nullable().optional(),
});

const ALLOWED_CATEGORIES = [
  'System',
  'Mission',
  'Power',
  'Comms',
  'DAA',
  'Surveillance',
  'Infrastructure',
  'OI',
] as const;

// Alert category `C2` maps to the internal `Comms` bucket (no separate C2).
function normCategory(c: string): EventEnvelope['category'] {
  const mapped = c === 'C2' ? 'Comms' : c;
  return (ALLOWED_CATEGORIES as readonly string[]).includes(mapped)
    ? (mapped as EventEnvelope['category'])
    : 'System';
}

// Events use ok|warn|crit; alerts use critical|warning|info. Normalise both.
function normSeverity(s: string): EventEnvelope['severity'] {
  const map: Record<string, EventEnvelope['severity']> = {
    critical: 'crit',
    crit: 'crit',
    warning: 'warn',
    warn: 'warn',
    info: 'info',
    ok: 'ok',
  };
  return map[s] ?? 'info';
}

// Alert severity keeps the doc's own scale (critical|warning|info).
function normAlertSeverity(s: string): AlertSeverity {
  const map: Record<string, AlertSeverity> = {
    critical: 'critical',
    crit: 'critical',
    warning: 'warning',
    warn: 'warning',
    info: 'info',
    ok: 'info',
  };
  return map[s] ?? 'info';
}

/** Map a `.../drones/events` payload → EventEnvelope (or null if malformed). */
export function parseDocEvent(raw: unknown): EventEnvelope | null {
  const r = DocEventPayload.safeParse(raw);
  if (!r.success) return null;
  const p = r.data;
  const data = (p.data ?? {}) as Record<string, unknown>;
  const distM = typeof data.distM === 'number' ? data.distM : null;
  const built = {
    // Events carry no id in the doc — synthesise a stable dedupe key.
    event_id: `${p.droneId}:${p.ts}:${p.eventType}`,
    drone_id: p.droneId,
    timestamp: new Date(p.ts).toISOString(),
    category: normCategory(p.category),
    severity: normSeverity(p.severity),
    message: p.message,
    conflict_target:
      (typeof data.targetCallsign === 'string' && data.targetCallsign) ||
      (typeof data.targetHex === 'string' && data.targetHex) ||
      null,
    ttc_sec: null,
    dist_km: distM != null ? distM / 1000 : null,
    ack_required: false,
    acknowledged: false,
  };
  const v = EventEnvelope.safeParse(built);
  return v.success ? v.data : null;
}

/** Map an `.../alerts[/severity]` or broadcast payload → AlertEnvelope. */
export function parseDocAlert(raw: unknown): AlertEnvelope | null {
  const r = DocAlertPayload.safeParse(raw);
  if (!r.success) return null;
  const p = r.data;
  const built = {
    alert_id: p.alertId,
    drone_id: p.droneId ?? null,
    timestamp: new Date(p.ts).toISOString(),
    // Alert category keeps `C2` etc. — normCategory would fold C2→Comms.
    category: p.category,
    severity: normAlertSeverity(p.severity),
    title: p.title ?? p.category,
    message: p.message,
    source_event: p.sourceEvent ?? null,
    requires_ack: Boolean(p.requiresAck),
    acked_at: typeof p.ackedAt === 'number' ? new Date(p.ackedAt).toISOString() : null,
    acked_by: p.ackedBy ?? null,
  };
  const v = AlertEnvelope.safeParse(built);
  return v.success ? v.data : null;
}
